import { Localized, Skill } from './types';

export const LANGUAGES = [
  { code: 'fr', label: 'Français' },
  { code: 'en', label: 'English' },
];

export function localized(value: Localized | null | undefined, lang: string): string {
  if (!value) return '';
  return value[lang] || value['en'] || value['fr'] || '';
}

/** Project title in the given language; `title` is the base/fallback. */
export function titleOf(p: { title: string; titles?: Localized }, lang: string): string {
  return p.titles?.[lang]?.trim() || p.title;
}

/** True if localized rich-text field has actual content (ignores empty Quill HTML like <p><br></p>) */
export function hasRichContent(value: Localized | null | undefined, lang: string): boolean {
  const html = localized(value, lang);
  if (!html) return false;
  return html.replace(/<[^>]*>/g, '').trim().length > 0;
}

/** Parse DD/MM/YYYY to timestamp for sorting */
export function parseDateStr(d: string): number {
  const [dd, mm, yyyy] = d.split('/');
  return new Date(`${yyyy}-${mm}-${dd}`).getTime();
}

/** Parse DD/MM/YYYY to YYYY-MM-DD for date inputs */
export function toInputDate(d: string | null | undefined): string {
  if (!d) return '';
  const [dd, mm, yyyy] = d.split('/');
  return `${yyyy}-${mm}-${dd}`;
}

export function slugify(text: string): string {
  return text
    .toLowerCase()
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '');
}

export function mediaUrl(stored: string | null | undefined, legacyPrefix: string): string | null {
  if (!stored) return null;
  if (/^(https?|blob):/.test(stored)) return stored;
  return `${legacyPrefix}/${stored}`;
}

/** Year part of a DD/MM/YYYY date */
export function yearOf(d: string | null | undefined): string {
  return d?.split('/')[2] ?? '';
}

/** Short pitch: first paragraph of a rich-text description, cut on a word boundary. */
export function pitchOf(html: string, max = 160): string {
  const doc = new DOMParser().parseFromString(html, 'text/html');
  const first = Array.from(doc.querySelectorAll('p')).map(p => p.textContent?.trim() ?? '').find(Boolean)
    ?? doc.body.textContent?.trim() ?? '';
  return first.length <= max ? first : `${first.slice(0, first.lastIndexOf(' ', max))}…`;
}

const STACK_CATEGORIES = ['Langage', 'Technologie'];

/** Stack shown in lists: languages first, then technologies, capped at `max`. */
export function keySkills(skills: Skill[], lang: string, max = 5): string[] {
  return STACK_CATEGORIES
    .flatMap(cat => skills.filter(s => s.category?.fr === cat))
    .slice(0, max)
    .map(s => localized(s.name, lang));
}
